// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { strings, interpolate } from "../strings";
import { getPrimaryRedTheme } from "../themes";
import { getTagCategory } from "../../Utils/utils";
import {
    IRegion, ITag, ILabel, FieldType, FieldFormat,
    TagInputMode, FeatureCategory, ITableTag,
    ITableRegion, ITableLabel
} from "../../model/applicationState";
import "./tagInput.scss";
import React from "react";
import { constants } from "../constants";
import Confirm from "../../common/confirm/confirm";
import "./tagInput.scss";
import TagInputItem, { ITagClickProps, ITagInputItemProps } from "./tagInputItem";
import { toast } from "react-toastify";
import TableTagLabeling from "./tableTagLabeling";

export enum TagOperationMode {
    None,
    ContextualMenu,
    Rename,
    LabelTable,
}

export interface ITagInputProps {
    /** Current list of tags */
    tags: ITag[];
    /** Currently selected regions in canvas */
    selectedRegions?: IRegion[];
    /** The labels in the canvas */
    labels: ILabel[];
    encoded?: boolean;
    /** The tableLabels in the canvas */
    tableLabels?: ITableLabel[];
    /** The doc current page number */
    pageNumber: number;
    /** Tags that are currently locked for editing experience */
    lockedTags?: string[];
    /** Updates to locked tags */
    onLockedTagsChange?: (locked: string[]) => void;
    /** Show tags when loaded */
    tagsLoaded: boolean;
    /** Place holder for input text box */
    placeHolder?: string;
    /** Function to call on clicking individual tag */
    onTagClick?: (tag: ITag) => void;
    /** Function to call on clicking individual tag while holding CTRL key */
    onCtrlTagClick?: (tag: ITag) => void;
    /** Function to call when tag is renamed */
    onTagRename?: (oldTag: ITag, newTag: ITag, cancelCallback: () => void) => void;
    /** Function to call when tag is deleted */
    onTagDeleted?: (tagName: string, tagType: FieldType, tagFormat: FieldFormat) => void;
    /** Always show tag input box */
    showTagInputBox?: boolean;
    /** Always show tag search box */
    showSearchBox?: boolean;
    /** Callback function for TagInputItemLabel mouse enter */
    onLabelEnter: (label: ILabel) => void;
    /** Callback function for TagInputItemLabel mouse leave */
    onLabelLeave: (label: ILabel) => void;
    /** Function to handle tag change */
    onTagChanged?: (oldTag: ITag, newTag: ITag) => void;
    updatePredictionResult?: (data: any, field: any) => void;
    setTagInputMode?: (tagInputMode: TagInputMode, selectedTableTagToLabel?: ITableTag) => void;
    tagInputMode: TagInputMode;
    selectedTableTagToLabel: ITableTag;
    handleLabelTable: (tagInputMode: TagInputMode, selectedTableTagToLabel) => void;
    addRowToDynamicTable: () => void;
    deleteRowFromDynamicTable: (rowIndex: number, tag: ITableTag) => void;
    handleTableCellClick: (iTableCellIndex, jTableCellIndex) => void;
    selectedTableTagBody: ITableRegion[][][];
    handleTableCellMouseEnter: (regions) => void;
    handleTableCellMouseLeave: () => void;
    splitPaneWidth: number;
    onTagDoubleClick?: (label: ILabel) => void;
    onTagLabelEdit: (tagName: string, text: string) => void;
    onTableTagLabelEdit: (tagName: string, metaData: object) => void;
    predictionResult: any;
    submitValidation: () => void;
    changePaneOrientation: () => void;
}

export interface ITagInputState {
    tags: ITag[];
    tagOperation: TagOperationMode;
    addTags: boolean;
    onlyCurrentPageTags: boolean;
    showOriginLabels: boolean;
    searchTags: boolean;
    searchQuery: string;
    selectedTag: ITag;
}

function isNameEqual(x: string, y: string) {
    return x.trim().toLocaleLowerCase() === y.trim().toLocaleLowerCase();
}

export class TagInput extends React.Component<ITagInputProps, ITagInputState> {
    public state: ITagInputState = {
        tags: this.props.tags || [],
        tagOperation: TagOperationMode.None,
        addTags: this.props.showTagInputBox,
        searchTags: this.props.showSearchBox,
        searchQuery: "",
        selectedTag: null,
        onlyCurrentPageTags: false,
        showOriginLabels: constants.showOriginLabelsByDefault,
    };

    private tagItemRefs: Map<string, TagInputItem> = new Map<string, TagInputItem>();
    private replaceConfirmRef = React.createRef<Confirm>();
    public componentDidUpdate(prevProps: ITagInputProps) {
        if (prevProps.tags !== this.props.tags) {
            let selectedTag = this.state.selectedTag;
            if (selectedTag) {
                selectedTag = this.props.tags.find((tag) => isNameEqual(tag.name, selectedTag.name));
            }

            this.setState({
                tags: this.props.tags,
                selectedTag,
            });
        }

        if (prevProps.selectedRegions !== this.props.selectedRegions && this.props.selectedRegions.length > 0) {
            this.setState({
                selectedTag: null,
            });
        }
    }

    public render() {
        if (this.props.tagInputMode === TagInputMode.LabelTable) {
            return (
                <div className="tag-input">
                    <TableTagLabeling
                        onTagClick={this.props.onTagClick}
                        selectedRegions={this.props.selectedRegions}
                        setTagInputMode={this.props.setTagInputMode}
                        selectedTag={this.props.selectedTableTagToLabel as ITableTag}
                        handleTableCellClick={this.props.handleTableCellClick}
                        handleTableCellMouseEnter={this.props.handleTableCellMouseEnter}
                        handleTableCellMouseLeave={this.props.handleTableCellMouseLeave}
                        selectedTableTagBody={this.props.selectedTableTagBody}
                        splitPaneWidth={this.props.splitPaneWidth}
                        addRowToDynamicTable={this.props.addRowToDynamicTable}
                        onTableTagLabelEdit={this.props.onTableTagLabelEdit}
                        deleteRowFromDynamicTable={this.props.deleteRowFromDynamicTable}
                        predictionResult={this.props.predictionResult}
                        updatePredictionResult={this.props.updatePredictionResult}
                        changePaneOrientation={this.props.changePaneOrientation}
                    />
                </div>
            )
        } else {
            return (
                <div className="tag-input">
                    <div className="tag-input-body-container">
                        <div className="tag-input-body">
                            <div className="tag-input-items">
                                {this.renderTagItems()}
                            </div>
                        </div>
                    </div>
                    <Confirm
                        title={strings.tags.warnings.replaceAllExitingLabelsTitle}
                        ref={this.replaceConfirmRef}
                        message={strings.tags.warnings.replaceAllExitingLabels}
                        confirmButtonTheme={getPrimaryRedTheme()}
                        onConfirm={this.onReplaceConfirm}
                    />
                    <button className='btn-success submit-validation' onClick={this.props.submitValidation}>Submit Validation</button>
                </div>
            );
        }
    }

    private onReplaceConfirm = (tag: ITag, props: ITagClickProps) => {
        const { onTagClick } = this.props;
        const { selectedTag, tagOperation: oldTagOperation } = this.state;
        const selected = selectedTag && isNameEqual(selectedTag.name, tag.name);
        const tagOperation = selected ? oldTagOperation : TagOperationMode.None;
        const deselect = selected && oldTagOperation === TagOperationMode.None;
        onTagClick(tag);
        this.setState({
            selectedTag: deselect ? null : tag,
            tagOperation,
        });
    }

    private renderTagItems = () => {
        let props = this.createTagItemProps();
        const query = this.state.searchQuery;
        this.tagItemRefs.clear();

        if (query.length) {
            props = props.filter((prop) => prop.tag.name.toLowerCase().includes(query.toLowerCase()));
        }

        return props.map((prop) =>
            <TagInputItem
                {...prop}
                key={prop.tag.name}
                labels={this.setTagLabels(prop.tag)}
                predictionResult={this.props.predictionResult}
                showOriginLabels={this.state.showOriginLabels}
                ref={(item) => this.setTagItemRef(item, prop.tag)}
                onLabelEnter={this.props.onLabelEnter}
                onLabelLeave={this.props.onLabelLeave}
                handleLabelTable={this.props.handleLabelTable}
                onTagDoubleClick={this.props.onTagDoubleClick}
                onTagLabelEdit={this.props.onTagLabelEdit}
                updatePredictionResult={this.props.updatePredictionResult}
            />);
    }

    private setTagItemRef = (item: TagInputItem, tag: ITag) => {
        this.tagItemRefs.set(tag.name, item);
        return item;
    }

    private setTagLabels = (tag: ITag): any[] => {
        const key = tag.name;
        let labels = this.props.labels.filter((label) => {
            if (this.props.encoded) {
                return label.label.replace(/~1/g, "/").replace(/~0/g, "~") === key;
            } else {
                return label.label === key;
            }
        });
        if (!tag.parentName && labels.length === 0) {
            const newLabel = { label: key, value: [] };
            this.props.labels.push(newLabel);
            labels = [newLabel];
        }
        labels && labels.forEach(l => {
            if (!l.value) {
                l.value = [];
            }
        });
        const tableLables = this.props.tableLabels.filter((label) => label.tableKey === key);
        return [...labels, ...tableLables]
    }

    private createTagItemProps = (): ITagInputItemProps[] => {
        const { tags, selectedTag, tagOperation, onlyCurrentPageTags } = this.state;
        const selectedRegionTagSet = this.getSelectedRegionTagSet();

        if (onlyCurrentPageTags) {
            const labels = this.props.labels.filter(item => item.value[0]?.page === this.props.pageNumber).map(item => item.label);
            const tableLabels = this.props.tableLabels.filter(item => item.labels[0]?.value[0]?.page === this.props.pageNumber).map(item => item.tableKey);
            const labeledTags = [...labels, ...tableLabels];

            if (labeledTags.length) {
                return tags.filter(tag => labeledTags.find(a => a === tag.name))
                    .map<ITagInputItemProps>(tag => {
                        return {
                            tag,
                            index: tags.findIndex((t) => isNameEqual(t.name, tag.name)),
                            isLocked: this.props.lockedTags
                                && this.props.lockedTags.findIndex((str) => isNameEqual(tag.name, str)) > -1,
                            isRenaming: selectedTag && isNameEqual(selectedTag.name, tag.name)
                                && tagOperation === TagOperationMode.Rename,
                            isSelected: selectedTag && isNameEqual(selectedTag.name, tag.name),
                            appliedToSelectedRegions: selectedRegionTagSet.has(tag.name),
                            onClick: this.onTagItemClick,
                        } as ITagInputItemProps;
                    });
            }
            return [];
        }

        return tags.map((tag) => (
            {
                tag,
                index: tags.findIndex((t) => isNameEqual(t.name, tag.name)),
                isLocked: this.props.lockedTags
                    && this.props.lockedTags.findIndex((str) => isNameEqual(tag.name, str)) > -1,
                isRenaming: selectedTag && isNameEqual(selectedTag.name, tag.name)
                    && tagOperation === TagOperationMode.Rename,
                isSelected: selectedTag && isNameEqual(selectedTag.name, tag.name),
                appliedToSelectedRegions: selectedRegionTagSet.has(tag.name),
                onClick: this.onTagItemClick,
            } as ITagInputItemProps
        ));
    }

    private getSelectedRegionTagSet = (): Set<string> => {
        const result = new Set<string>();
        if (this.props.selectedRegions) {
            for (const region of this.props.selectedRegions) {
                for (const tag of region.tags) {
                    result.add(tag);
                }
            }
        }
        return result;
    }

    private onTagItemClick = (tag: ITag, props: ITagClickProps) => {
        if (props.ctrlKey && this.props.onCtrlTagClick) { // Lock tags
            this.props.onCtrlTagClick(tag);
        } else if (props.clickedDropDown) {
            const { selectedTag } = this.state;
            const showContextualMenu = !selectedTag || !isNameEqual(selectedTag.name, tag.name)
                || this.state.tagOperation !== TagOperationMode.ContextualMenu;
            const tagOperation = showContextualMenu ? TagOperationMode.ContextualMenu : TagOperationMode.None;
            this.setState({
                selectedTag: tag,
                tagOperation,
            });
        } else { // Select tag
            const { selectedTag, tagOperation: oldTagOperation } = this.state;
            const selected = selectedTag && isNameEqual(selectedTag.name, tag.name);
            const tagOperation = selected ? oldTagOperation : TagOperationMode.None;
            let deselect = selected && oldTagOperation === TagOperationMode.None;

            // Only fire click event if a region is selected
            const { selectedRegions, onTagClick, labels } = this.props;
            if (selectedRegions && selectedRegions.length && onTagClick) {
                const { category } = selectedRegions[0];
                const { format, type, documentCount, name } = tag;
                const tagCategory = getTagCategory(type);
                const isTagLabelTypeDrawnRegion = this.labelAssignedDrawnRegion(labels, tag.name);
                const labelAssigned = this.labelAssigned(labels, name);

                if ((tag.type === FieldType.Object || tag.type === FieldType.Array) && this.props.selectedRegions?.length) {
                    this.props.handleLabelTable(TagInputMode.LabelTable, tag)
                    deselect = false;
                } else if (labelAssigned && ((category === FeatureCategory.DrawnRegion) !== isTagLabelTypeDrawnRegion)) {
                    if (category === FeatureCategory.Checkbox && isTagLabelTypeDrawnRegion) {
                        toast.warn(interpolate(strings.tags.warnings.notCompatibleWithDrawnRegionTag, { otherCategory: FeatureCategory.Checkbox }));
                    } else if (isTagLabelTypeDrawnRegion) {
                        this.replaceConfirmRef.current.open(tag, props);
                    } else if (tagCategory === FeatureCategory.Checkbox) {
                        toast.warn(interpolate(strings.tags.warnings.notCompatibleWithDrawnRegionTag, { otherCategory: FeatureCategory.Checkbox }));
                    } else {
                        this.replaceConfirmRef.current.open(tag, props);
                    }
                    return;
                } else if (tagCategory === category || category === FeatureCategory.DrawnRegion ||
                    (documentCount === 0 && type === FieldType.String && format === FieldFormat.NotSpecified)) {
                    if (tagCategory === FeatureCategory.Checkbox && labelAssigned) {
                        toast.warn(strings.tags.warnings.checkboxPerTagLimit);
                        return;
                    }
                    if (tagCategory === FeatureCategory.Checkbox && !(category === FeatureCategory.Checkbox || category === FeatureCategory.DrawnRegion)) {
                        toast.warn(strings.tags.warnings.notCompatibleTagType);
                        return;
                    }
                    onTagClick(tag);
                    deselect = false;
                } else {
                    toast.warn(strings.tags.warnings.notCompatibleTagType, { autoClose: 7000 });
                }
            }
            this.setState({
                selectedTag: deselect ? null : tag,
                tagOperation,
            });
        }
    }
    focusTag(tag: string) {
        const tagItemRef = this.tagItemRefs.get(tag)?.getTagNameRef();
        if (tagItemRef) {
            tagItemRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
            tagItemRef.current.classList.add("tag-item-highlight");
            setTimeout(() => {
                tagItemRef.current.classList.remove("tag-item-highlight");
            }, 2000);
        }
    }
    public labelAssigned = (labels: ILabel[], name): boolean => {
        const label = labels?.find((label) => label.label === name ? true : false);
        if (!label) {
            return false;
        } else {
            return true;
        }
    }

    public labelAssignedDrawnRegion = (labels: ILabel[], name): boolean => {
        const label = labels?.find((label) => label.label === name ? true : false);
        if (label?.labelType === FeatureCategory.DrawnRegion) {
            return true;
        } else {
            return false;
        }
    }
}
